import React, { useState, useRef, useEffect, useCallback } from 'react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import './loadboard.css'
import '../Autocomplete/autocomplete.css'
import { Filter, X, ChevronsUpDown } from 'lucide-react'

import SavedSearchesControls from './SavedSearchControls'
import NotificationControls from './NotificationControls'
import SortyByLoads from './SortyByLoads'
import ToggleFilter from './ToggleFilter'

import OriginFilters from './Filters/OriginFilters'
import DestinationFilterWrapper from './Filters/DestinationFilterWrapper'
import UserSearches from './Searches/UserSearches'
import UserNewSearch from './Searches/UserNewSearch'

function FilterFieldShell({ label, isDarkMode, children }) {
	return (
		<div
			className={`relative flex items-center rounded-lg border px-3 h-9 transition-colors focus-within:border-sky-500 ${
				isDarkMode ? 'bg-white/[0.03] border-white/10' : 'bg-white border-gray-200'
			}`}
		>
			<span
				className={`absolute -top-[7px] left-2.5 z-[1] px-1 text-[10px] font-medium leading-none ${
					isDarkMode ? 'text-gray-500' : 'text-gray-400'
				}`}
				style={{ backgroundColor: isDarkMode ? '#22272d' : '#ffffff' }}
			>
				{label}
			</span>
			<div className="w-full min-w-0">{children}</div>
		</div>
	)
}

function FilterSelect({
	name,
	value,
	onChange,
	disabled,
	title,
	isDarkMode,
	children,
}) {
	return (
		<div className="relative">
			<select
				name={name}
				value={value}
				onChange={onChange}
				disabled={disabled}
				title={title}
				style={{
					colorScheme: isDarkMode ? 'dark' : 'light',
					WebkitAppearance: 'none',
					MozAppearance: 'none',
					appearance: 'none',
					backgroundImage: 'none',
				}}
				className={`w-full appearance-none bg-transparent border-0 p-0 pr-5 text-[13px] leading-tight focus:outline-none focus:ring-0 ${
					disabled ? 'cursor-not-allowed' : 'cursor-pointer'
				} ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}
			>
				{children}
			</select>
			<ChevronsUpDown className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
		</div>
	)
}

function FilterNumber({
	name,
	value,
	onChange,
	onBlur,
	prefix,
	suffix,
	placeholder,
	isDarkMode,
	step,
	min,
}) {
	const hasStepper = step !== undefined
	return (
		<div className="flex items-center">
			{prefix && (
				<span
					className={`mr-1 text-[13px] ${
						isDarkMode ? 'text-gray-500' : 'text-gray-400'
					}`}
				>
					{prefix}
				</span>
			)}
			<input
				type="number"
				name={name}
				value={value}
				onChange={onChange}
				onBlur={onBlur}
				placeholder={placeholder}
				step={step}
				min={min}
				style={hasStepper ? { colorScheme: isDarkMode ? 'dark' : 'light' } : undefined}
				className={`${
					hasStepper ? 'lf-filter-num-step' : 'lf-filter-num'
				} w-full min-w-0 bg-transparent border-0 p-0 text-[13px] leading-tight focus:outline-none focus:ring-0 placeholder:text-gray-400 ${
					isDarkMode ? 'text-gray-100' : 'text-gray-900'
				}`}
			/>
			{suffix && (
				<span
					className={`ml-1 text-[13px] ${
						isDarkMode ? 'text-gray-500' : 'text-gray-400'
					}`}
				>
					{suffix}
				</span>
			)}
		</div>
	)
}

function FilterToggle({ label, checked, onClick, isDarkMode }) {
	return (
		<button
			type="button"
			onClick={onClick}
			className="flex items-center gap-2.5"
		>
			<span
				className={`text-[13px] font-medium ${
					isDarkMode ? 'text-gray-200' : 'text-gray-800'
				}`}
			>
				{label}
			</span>
			<span
				className="relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors"
				style={{ backgroundColor: checked ? '#20b1a5' : '#acbac7' }}
			>
				<span
					className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
						checked ? 'translate-x-[18px]' : 'translate-x-0.5'
					}`}
				/>
			</span>
		</button>
	)
}

const getGradesAboveAndEqual = selectedGrade => {
	const grades = [
		'A_PLUS',
		'A',
		'B_PLUS',
		'B',
		'C_PLUS',
		'C',
		'D_PLUS',
		'D',
		'F',
	]
	const selectedIndex = grades.indexOf(selectedGrade)
	return selectedIndex >= 0 ? grades.slice(0, selectedIndex + 1) : []
}

export default function LoadBoardFilters(props) {
	const {
		filters,
		setFilters,
		isDarkMode,
		handleManualRefresh,
		handleAutoRefresh,
		handleButtonClick,
		botDetected,
		resetFilters,
		isAudioOn,
		setAudioOn,
		setIsAutoRefresh,
		isAutoRefresh,
		moveResultsToTop,
		setMoveResultsToTop,
		refreshInterval,
		setRefreshInterval,
		isFetching,
		userType,
		isAdmin,
		continueOnAlert,
		setContinueOnAlert,
		showQuickPost,
		setShowQuickPost,
		contentWidth,
		setContentWidth,
		viewMode,
		setViewMode,
		sidebarIconMode,
		setSidebarIconMode,
	} = props

	const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(true)
	const [isSearchModalOpen, setIsSearchModalOpen] = useState(false)
	const [isNewSearchModalOpen, setIsNewSearchModalOpen] = useState(false)

	// Local state for pending filter changes (apply on blur or dropdown close)
	const [localDistance, setLocalDistance] = useState(filters.distance)
	const [localMaxDistance, setLocalMaxDistance] = useState(filters.maxdistance)
	const [localPricePerMile, setLocalPricePerMile] = useState(
		filters.pricePerMile || ''
	)
	const [localPrice, setLocalPrice] = useState(filters.price || '')
	const [hasPendingChanges, setHasPendingChanges] = useState(false)
	const [refreshCycle, setRefreshCycle] = useState(0)
	// Countdown is owned here so the per-second tick re-renders only the filter
	// bar, not the whole board (which was re-rendering every LoadCard each second).
	const [countdown, setCountdown] = useState(refreshInterval)
	const [countdownProgress, setCountdownProgress] = useState(0)
	const refreshCycleStartRef = useRef(null)
	const refreshTimerRef = useRef(null)
	const refreshInProgressRef = useRef(false)
	const handleRefreshRef = useRef(null)
	const wasFetchingRef = useRef(false)

	// Sync local state when filters change externally (e.g., from reset or saved search)
	useEffect(() => {
		setLocalDistance(filters.distance)
		setLocalMaxDistance(filters.maxdistance)
		setLocalPricePerMile(filters.pricePerMile || '')
		setLocalPrice(filters.price || '')
		setHasPendingChanges(false)
	}, [
		filters.distance,
		filters.maxdistance,
		filters.pricePerMile,
		filters.price,
	])

	// Apply pending filter changes
	const applyPendingChanges = useCallback(() => {
		if (hasPendingChanges) {
			setFilters(prev => ({
				...prev,
				paginateSize: 1,
				distance: localDistance,
				maxdistance: localMaxDistance,
				pricePerMile: localPricePerMile,
				price: localPrice,
			}))
			setHasPendingChanges(false)
		}
	}, [
		hasPendingChanges,
		localDistance,
		localMaxDistance,
		localPricePerMile,
		localPrice,
		setFilters,
	])

	// Memoized callbacks to prevent infinite re-renders
	const handlePickupLocationsChange = useCallback(
		locations => {
			setFilters(prev => ({ ...prev, pickupLocations: locations }))
		},
		[setFilters]
	)

	const handleDropoffLocationsChange = useCallback(
		locations => {
			setFilters(prev => ({ ...prev, dropoffLocations: locations }))
		},
		[setFilters]
	)

	const handleRefresh = async (source = 'button') => {
		if (refreshInProgressRef.current) return

		refreshInProgressRef.current = true
		setFilters(prevFilters =>
			prevFilters.paginateSize === 1
				? prevFilters
				: { ...prevFilters, paginateSize: 1 }
		)

		try {
			if (source === 'auto') {
				await handleAutoRefresh()
			} else {
				await handleManualRefresh()
			}
		} finally {
			refreshInProgressRef.current = false
			refreshCycleStartRef.current = null
			setCountdown(refreshInterval)
			setCountdownProgress(0)
			setRefreshCycle(cycle => cycle + 1)
		}
	}

	handleRefreshRef.current = handleRefresh

	useEffect(() => {
		if (
			isAutoRefresh &&
			wasFetchingRef.current &&
			!isFetching &&
			!refreshInProgressRef.current
		) {
			refreshCycleStartRef.current = null
			setCountdown(refreshInterval)
			setCountdownProgress(0)
			setRefreshCycle(cycle => cycle + 1)
		}

		wasFetchingRef.current = isFetching
	}, [isAutoRefresh, isFetching, refreshInterval, setCountdown])

	useEffect(() => {
		refreshCycleStartRef.current = null
		setCountdown(refreshInterval)
		setCountdownProgress(0)
	}, [refreshInterval, setCountdown])

	useEffect(() => {
		if (refreshTimerRef.current) {
			clearInterval(refreshTimerRef.current)
			refreshTimerRef.current = null
		}

		if (!isAutoRefresh) {
			refreshCycleStartRef.current = null
			setCountdown(refreshInterval)
			setCountdownProgress(0)
			return
		}

		if (isFetching || refreshInProgressRef.current) return

		const intervalMs = Math.max(refreshInterval, 1) * 1000

		const tick = () => {
			const timestamp = performance.now()

			if (refreshCycleStartRef.current === null) {
				refreshCycleStartRef.current = timestamp
			}

			const elapsedMs = Math.max(timestamp - refreshCycleStartRef.current, 0)
			const progress = Math.min(elapsedMs / intervalMs, 1)
			const remainingMs = Math.max(intervalMs - elapsedMs, 0)

			setCountdown(Math.ceil(remainingMs / 1000))
			setCountdownProgress(progress)

			if (progress >= 1) {
				if (refreshTimerRef.current) {
					clearInterval(refreshTimerRef.current)
					refreshTimerRef.current = null
				}
				handleRefreshRef.current?.('auto')
			}
		}

		tick()
		refreshTimerRef.current = setInterval(tick, 1000)

		return () => {
			if (refreshTimerRef.current) {
				clearInterval(refreshTimerRef.current)
				refreshTimerRef.current = null
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAutoRefresh, isFetching, refreshInterval, refreshCycle])

	const dropdownRef = useRef(null)

	// Handle filter dropdown toggle with pending changes
	const handleFilterToggle = useCallback(() => {
		if (!isFiltersCollapsed) {
			// Closing dropdown - apply pending changes
			applyPendingChanges()
		}
		setIsFiltersCollapsed(!isFiltersCollapsed)
	}, [isFiltersCollapsed, applyPendingChanges])

	const handleDocumentClick = event => {
		if (event.target.closest('[data-filters-trigger]')) return
		if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
			applyPendingChanges()
			setIsFiltersCollapsed(true)
		}
	}

	// Close filter dropdown when user clicks outside of it
	useEffect(() => {
		document.addEventListener('mousedown', handleDocumentClick)

		return () => {
			document.removeEventListener('mousedown', handleDocumentClick)
		}
	}, [])

	const handleFilterChange = e => {
		const { name, value } = e.target

		setFilters({
			...filters,
			paginateSize: 1,
			[name]: value,
		})
	}

	const handleOldLoadsToggle = nextLimited => {
		const newLimited =
			typeof nextLimited === 'boolean' ? nextLimited : !filters.limited
		if (newLimited && isAutoRefresh) {
			setIsAutoRefresh(false)
		}
		setFilters({
			...filters,
			limited: newLimited,
			sortBy: newLimited ? 'newest' : 'pickupDate',
			paginateSize: 1,
		})
	}

	const handleClearFilters = () => {
		setHasPendingChanges(false)
		resetFilters()
	}

	const handleOriginChange = origin => {
		const pickupCity = origin?.city || ''
		const pickupState = origin?.state_id || ''
		const pickupStateName = origin?.state_id || ''
		const pickupLat = origin?.lat || ''
		const pickupLng = origin?.lng || ''

		setFilters(prevFilters => ({
			...prevFilters,
			paginateSize: 1,
			pickupCity,
			pickupState,
			pickupStateName,
			pickupLat,
			pickupLng,
			origin,
		}))
	}

	const handleDestinationChange = destination => {
		const dropLat = destination?.lat || ''
		const dropLng = destination?.lng || ''
		const dropCity = destination?.city || ''
		const dropState = destination?.state_id || ''
		const dropStateName = destination?.state_id || ''

		setFilters(prevFilters => ({
			...prevFilters,
			paginateSize: 1,
			dropLat,
			dropLng,
			dropCity,
			dropState,
			dropStateName,
			destination,
		}))
	}

	const styles = isDarkMode
		? {
				backgroundColor: '#363e47',
				color: '#C9D1D9',
				borderColor: '#30363D',
		  }
		: {
				backgroundColor: '#ffffff',
				color: '#000000',
				borderColor: '#d1d5db',
		  }

	const labels = isDarkMode
		? {
				color: '#C9D1D9',
				borderColor: '#30363D',
		  }
		: {
				color: '#000000',
				borderColor: '#d1d5db',
		  }

	const handleCheckboxToggle = (filterName, value) => {
		setFilters({
			...filters,
			paginateSize: 1,
			[filterName]: value,
		})
	}

	return (
		<div
			className="lb-pad-x px-4 sm:px-6 lg:px-8 pt-2.5 pb-0 md:pt-2.5 md:pb-0"
			style={{
				position: 'relative',
				backgroundColor: isDarkMode ? '#1c2126' : '#ffffff',
			}}
		>
			<div className="py-0">
				<div className="block md:hidden">
					<SavedSearchesControls
						openModal={() => setIsSearchModalOpen(true)}
						openNewSearchModal={() => setIsNewSearchModalOpen(true)}
						isDarkMode={isDarkMode}
					/>
				</div>
				{/* Top control panel */}
				<div className="flex justify-center md:justify-between mx-0 items-center flex-col md:flex-row mb-0 gap-2 md:gap-0 w-full">
					<div className="hidden md:block">
						<SavedSearchesControls
							openModal={() => setIsSearchModalOpen(true)}
							openNewSearchModal={() => setIsNewSearchModalOpen(true)}
							isDarkMode={isDarkMode}
						/>
					</div>
					<UserSearches
						isModalOpen={isSearchModalOpen}
						closeModal={() => setIsSearchModalOpen(false)}
						setFilters={setFilters}
						isDarkMode={isDarkMode}
						setRefreshInterval={setRefreshInterval}
					/>
					<UserNewSearch
						isModalOpen={isNewSearchModalOpen}
						closeModal={() => setIsNewSearchModalOpen(false)}
						filters={filters}
						isDarkMode={isDarkMode}
						refreshInterval={refreshInterval}
					/>
					<NotificationControls
						handleRefresh={handleRefresh}
						isAutoRefresh={isAutoRefresh}
						setIsAutoRefresh={setIsAutoRefresh}
						countdown={countdown}
						countdownProgress={countdownProgress}
						isDarkMode={isDarkMode}
						isAudioOn={isAudioOn}
						setAudioOn={setAudioOn}
						priceThreshold={filters.priceThreshold}
						setPriceThreshold={value =>
							setFilters(prev => ({ ...prev, priceThreshold: value }))
						}
						moveResultsToTop={moveResultsToTop}
						setMoveResultsToTop={setMoveResultsToTop}
						handleButtonClick={handleButtonClick}
						botDetected={botDetected}
						isFetching={isFetching}
						refreshInterval={refreshInterval}
						setRefreshInterval={setRefreshInterval}
						userType={userType}
						continueOnAlert={continueOnAlert}
						setContinueOnAlert={setContinueOnAlert}
						showQuickPost={showQuickPost}
						setShowQuickPost={setShowQuickPost}
						contentWidth={contentWidth}
						setContentWidth={setContentWidth}
						viewMode={viewMode}
						setViewMode={setViewMode}
						sidebarIconMode={sidebarIconMode}
						setSidebarIconMode={setSidebarIconMode}
					/>
				</div>

				{/* origin and destination fields start */}
				<div className="flex flex-col md:flex-row md:gap-2 text-gray-200">
					{/* Origin */}
					<OriginFilters
						filters={filters}
						handleOriginChange={handleOriginChange}
						labels={labels}
						handleFilterChange={handleFilterChange}
						styles={styles}
						isDarkMode={isDarkMode}
						onPickupLocationsChange={handlePickupLocationsChange}
					/>

					{/* Destination Filters */}
					<DestinationFilterWrapper
						filters={filters}
						setFilters={setFilters}
						handleDestinationChange={handleDestinationChange}
						handleFilterChange={handleFilterChange}
						labels={labels}
						styles={styles}
						isDarkMode={isDarkMode}
						onDropoffLocationsChange={handleDropoffLocationsChange}
					/>

					{/* Equipment */}
					<div className="equipment-select select-wrap flex md:w-[200px] md:shrink-0 lg:w-auto lg:min-w-[150px] lg:max-w-[200px] lg:flex-1">
						<label
							style={{
								...labels,
								backgroundColor: isDarkMode ? '#1c2126' : '#ffffff',
								color: isDarkMode ? '#C9D1D9' : '#3a4353',
							}}
						>
							Equipment
						</label>
						<select
							name="equipType"
							onChange={handleFilterChange}
							className=" mt-1 p-2 rounded-md w-full flex-grow"
							style={styles}
							firstvalue="Any"
							value={filters.equipType}
						>
							<option value="">Any</option>
							<option value={'POWER ONLY'}>Power Only</option>
							<option value={'DRY VAN'}>Dry Van</option>
						</select>
					</div>
				</div>
				{/* origin and destination fields end */}

				<div className="relative flex flex-wrap justify-between items-center gap-2 mt-2.5 md:mt-2.5">
					<button
						data-filters-trigger
						className={`inline-flex items-center gap-1.5 text-[13px] font-normal transition-colors ${
							isDarkMode
								? 'text-gray-200 hover:text-white'
								: 'text-gray-700 hover:text-gray-900'
						}`}
						onClick={handleFilterToggle}
					>
						<Filter className="h-4 w-4" />
						Filters
					</button>

					<div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-2 toggle-container">
						<div className="flex items-center gap-2">
							<label
								className={`whitespace-nowrap text-[13px] font-normal ${
									isDarkMode ? 'text-gray-200' : 'text-gray-700'
								}`}
							>
								{filters.limited ? 'Hide ' : 'Show '} Old Loads
							</label>
							<ToggleFilter
								show={filters.limited}
								setShow={handleOldLoadsToggle}
							/>
						</div>

						<div className="block rounded-xs">
							<SortyByLoads
								onChange={handleFilterChange}
								value={filters.sortBy}
								name="sortBy"
								isDarkMode={isDarkMode}
							/>
						</div>
					</div>

					{/* Dropdown Additional Filters */}
					<div
						ref={dropdownRef}
						className={`absolute top-full left-0 mt-1 w-full z-[1000] rounded-xl overflow-hidden ${
							isFiltersCollapsed ? 'hidden' : 'block'
						}`}
					style={{
						backgroundColor: isDarkMode ? '#22272d' : '#ffffff',
						border: isDarkMode
							? '1px solid rgba(255, 255, 255, 0.12)'
							: '1px solid #eef0f3',
						boxShadow: isDarkMode
							? '0 0 30px rgba(255, 255, 255, 0.08)'
							: '0 16px 40px rgba(0, 0, 0, 0.18)',
					}}
				>
					<style>{`
						.lf-filter-num::-webkit-outer-spin-button,
						.lf-filter-num::-webkit-inner-spin-button {
							-webkit-appearance: none;
							margin: 0;
						}
						.lf-filter-num { -moz-appearance: textfield; }
						.lf-filter-num-step { -moz-appearance: auto; }
						.lf-filter-num-step::-webkit-outer-spin-button,
						.lf-filter-num-step::-webkit-inner-spin-button {
							-webkit-appearance: auto;
							opacity: 1;
						}
					`}</style>

					{/* Header */}
					<div className="flex items-center justify-start px-3 pt-2 pb-1">
						<button
							type="button"
							onClick={() => {
								applyPendingChanges()
								setIsFiltersCollapsed(true)
							}}
							className={`transition-colors ${
								isDarkMode
									? 'text-gray-400 hover:text-white'
									: 'text-gray-400 hover:text-gray-700'
							}`}
							aria-label="Close filters"
						>
							<X className="h-5 w-5" />
						</button>
					</div>

					<div className="px-5 pt-2 pb-3">
						<div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-3 gap-y-7">
							<FilterFieldShell label="Performance" isDarkMode={isDarkMode}>
								<FilterSelect
									name="carrierGrade"
									isDarkMode={isDarkMode}
									onChange={e => {
										const selectedGrade = e.target.value
										const gradesAboveAndEqual = selectedGrade
											? getGradesAboveAndEqual(selectedGrade)
											: []
										setFilters({
											...filters,
											paginateSize: 1,
											carrierGrades: gradesAboveAndEqual,
										})
									}}
									value={
										filters.carrierGrades.length > 0
											? filters.carrierGrades[filters.carrierGrades.length - 1]
											: ''
									}
								>
									<option value="">Any</option>
									<option value="A_PLUS">A+</option>
									<option value="A">A & up</option>
									<option value="B_PLUS">B+ & up</option>
									<option value="B">B & up</option>
									<option value="C_PLUS">C+ & up</option>
									<option value="C">C & up</option>
									<option value="D_PLUS">D+ & up</option>
									<option value="D">D & up</option>
									<option value="F">F & up</option>
								</FilterSelect>
							</FilterFieldShell>

							<FilterFieldShell label="Max stops" isDarkMode={isDarkMode}>
								<FilterSelect
									name="stopCount"
									isDarkMode={isDarkMode}
									onChange={handleFilterChange}
									value={filters.stopCount}
								>
									<option value="0">Any</option>
									<option value="2">2 stops</option>
									<option value="3">3 stops</option>
									<option value="4">4 stops</option>
									<option value="5">5 stops</option>
									<option value="6">6 stops</option>
									<option value="7">7 stops</option>
									<option value="8">8 stops</option>
									<option value="9">9 stops</option>
								</FilterSelect>
							</FilterFieldShell>

							<FilterFieldShell label="Distance (min)" isDarkMode={isDarkMode}>
								<FilterNumber
									name="distance"
									isDarkMode={isDarkMode}
									suffix="mi"
									placeholder="Any"
									step="50"
									min="0"
									value={localDistance}
									onChange={e => {
										setLocalDistance(e.target.value)
										setHasPendingChanges(true)
									}}
									onBlur={applyPendingChanges}
								/>
							</FilterFieldShell>

							<FilterFieldShell label="Distance (max)" isDarkMode={isDarkMode}>
								<FilterNumber
									name="maxdistance"
									isDarkMode={isDarkMode}
									suffix="mi"
									placeholder="Any"
									step="50"
									min="0"
									value={localMaxDistance}
									onChange={e => {
										setLocalMaxDistance(e.target.value)
										setHasPendingChanges(true)
									}}
									onBlur={applyPendingChanges}
								/>
							</FilterFieldShell>

							<FilterFieldShell label="Driver Type" isDarkMode={isDarkMode}>
								<FilterSelect
									name="driverType"
									isDarkMode={isDarkMode}
									onChange={handleFilterChange}
									value={filters.driverType || ''}
								>
									<option value="">Any</option>
									<option value="Solo">Solo</option>
									<option value="Team">Team</option>
								</FilterSelect>
							</FilterFieldShell>

							<FilterFieldShell label="Last seen" isDarkMode={isDarkMode}>
								<FilterSelect
									name="lastSeen"
									isDarkMode={isDarkMode}
									onChange={handleFilterChange}
									value={filters.lastSeen || 3}
									disabled={filters.limited}
									title={
										filters.limited
											? 'Disable "Hide old loads" to use this filter'
											: ''
									}
								>
									<option value={2}>2 minutes</option>
									<option value={3}>3 minutes</option>
									<option value={4}>4 minutes</option>
									<option value={5}>5 minutes</option>
									<option value={6}>6 minutes</option>
									<option value={7}>7 minutes</option>
									<option value={8}>8 minutes</option>
								</FilterSelect>
							</FilterFieldShell>

							<FilterFieldShell label="Min per mile" isDarkMode={isDarkMode}>
								<FilterNumber
									name="pricePerMile"
									isDarkMode={isDarkMode}
									prefix="$"
									suffix="/mi"
									placeholder="Any"
									step="0.10"
									min="0"
									value={localPricePerMile}
									onChange={e => {
										setLocalPricePerMile(e.target.value)
										setHasPendingChanges(true)
									}}
									onBlur={applyPendingChanges}
								/>
							</FilterFieldShell>

							<FilterFieldShell label="Min payout" isDarkMode={isDarkMode}>
								<FilterNumber
									name="price"
									isDarkMode={isDarkMode}
									prefix="$"
									placeholder="Any"
									step="50"
									min="0"
									value={localPrice}
									onChange={e => {
										setLocalPrice(e.target.value)
										setHasPendingChanges(true)
									}}
									onBlur={applyPendingChanges}
								/>
							</FilterFieldShell>

							<FilterFieldShell label="Pickup Date (from)" isDarkMode={isDarkMode}>
								<DatePicker
									selected={
										filters.pickupDateFrom
											? new Date(filters.pickupDateFrom)
											: null
									}
									onChange={date => {
										setFilters(prev => ({
											...prev,
											paginateSize: 1,
											pickupDateFrom: date ? date.toISOString() : '',
										}))
									}}
									showTimeSelect
									timeIntervals={15}
									timeFormat="HH:mm"
									dateFormat="MM/dd/yyyy HH:mm"
									placeholderText="Select date & time"
									isClearable
									popperProps={{ strategy: 'fixed' }}
									popperClassName="lf-dp-popper"
									calendarClassName={`lf-dp ${isDarkMode ? 'lf-dp-dark' : ''}`}
									className={`w-full bg-transparent border-0 p-0 text-[13px] leading-tight focus:outline-none focus:ring-0 placeholder:text-gray-400 ${
										isDarkMode ? 'text-gray-100' : 'text-gray-900'
									}`}
									wrapperClassName="w-full"
								/>
							</FilterFieldShell>

							<FilterFieldShell label="Delivery Date (to)" isDarkMode={isDarkMode}>
								<DatePicker
									selected={
										filters.deliveryDateTo
											? new Date(filters.deliveryDateTo)
											: null
									}
									onChange={date => {
										setFilters(prev => ({
											...prev,
											paginateSize: 1,
											deliveryDateTo: date ? date.toISOString() : '',
										}))
									}}
									showTimeSelect
									timeIntervals={15}
									timeFormat="HH:mm"
									dateFormat="MM/dd/yyyy HH:mm"
									placeholderText="Select date & time"
									isClearable
									popperProps={{ strategy: 'fixed' }}
									popperClassName="lf-dp-popper"
									calendarClassName={`lf-dp ${isDarkMode ? 'lf-dp-dark' : ''}`}
									className={`w-full bg-transparent border-0 p-0 text-[13px] leading-tight focus:outline-none focus:ring-0 placeholder:text-gray-400 ${
										isDarkMode ? 'text-gray-100' : 'text-gray-900'
									}`}
									wrapperClassName="w-full"
								/>
							</FilterFieldShell>

							<FilterFieldShell label="Load Type" isDarkMode={isDarkMode}>
								<FilterSelect
									name="loadType"
									isDarkMode={isDarkMode}
									onChange={handleFilterChange}
									value={filters.loadType || ''}
								>
									<option value="">Any</option>
									<option value="Live">Live</option>
									<option value="Drop">Drop & Hook</option>
								</FilterSelect>
							</FilterFieldShell>

							<FilterFieldShell label="Work Type" isDarkMode={isDarkMode}>
								<FilterSelect
									name="workType"
									isDarkMode={isDarkMode}
									onChange={handleFilterChange}
									value={filters.workType || ''}
								>
									<option value="">Any</option>
									<option value="OneWay">One Way</option>
									<option value="RoundTrip">Round Trip</option>
								</FilterSelect>
							</FilterFieldShell>
						</div>

						{/* Toggles */}
						<div className="mt-7 flex flex-wrap items-center gap-x-8 gap-y-3">
							<FilterToggle
								label="Bobtail"
								checked={!!filters.bobtail}
								isDarkMode={isDarkMode}
								onClick={() => handleCheckboxToggle('bobtail', !filters.bobtail)}
							/>
							<FilterToggle
								label="AI Bookable"
								checked={!!filters.isAIEnabled}
								isDarkMode={isDarkMode}
								onClick={() =>
									handleCheckboxToggle('isAIEnabled', !filters.isAIEnabled)
								}
							/>
							{isAdmin && (
								<div className="w-40">
									<FilterFieldShell label="Certification" isDarkMode={isDarkMode}>
										<FilterSelect
											name="certRequired"
											isDarkMode={isDarkMode}
											onChange={handleFilterChange}
											value={filters.certRequired || ''}
										>
											<option value="">Any</option>
											<option value="required">Required</option>
											<option value="excluded">Not Required</option>
										</FilterSelect>
									</FilterFieldShell>
								</div>
							)}
						</div>

						<div
							className={`-mx-5 mt-5 flex justify-start border-t px-5 pt-3 ${
								isDarkMode ? 'border-white/10' : 'border-gray-200'
							}`}
						>
							<button
								type="button"
								className={`text-[13px] font-normal transition-colors ${
									isDarkMode
										? 'text-gray-300 hover:text-white'
										: 'text-gray-600 hover:text-gray-900'
								}`}
								onClick={handleClearFilters}
							>
								Clear Filters
							</button>
						</div>
					</div>

				</div>
				</div>
			</div>
		</div>
	)
}
